import { useState } from "react";
import { useTranslation } from "react-i18next";
import { message, Modal, InputNumber } from "antd";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { amountWithCommas, generateRandomId } from "../../utility/fn";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { depositBalance } from "../../server/User";
import { updateCredit } from "../../features/user/userSlice";

const RADNOM_PAYMENT_ID = generateRandomId();

export const DepositBalanceBtn = () => {
  const { t } = useTranslation();
  const [msgApi, contextHolder] = message.useMessage();
  const [despositBalanceModal, setDespositBalanceModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const credit = useAppSelector((state: any) => state.user.credit);
  const USD_TO_TWD = useAppSelector(
    (state: any) => state.settings.conversionRate,
  );
  const dispatch = useAppDispatch();

  // ✅ CHANGED: allow deposits smaller than 100 (set to 25 if you want the smallest preset)
  const depositSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError(t("Deposit amount is required"))
      .required(t("Deposit amount is required"))
      .min(1, t("Minimum credit is 1")), // or .min(25, "Minimum credit is 25")
    currency: Yup.string()
      .oneOf(["USD", "NTD"])
      .required(t("Currency is required")),
    payment_method: Yup.string()
      .oneOf(["PayPal", "Green"])
      .required(t("Payment method is required")),
  });

  const openDepositModal = () => setDespositBalanceModal(true);
  const handleCancel = () => setDespositBalanceModal(false);

  const handledTotalCost = (amount: number, currency: "USD" | "NTD") => {
    const treated = currency === "NTD" ? amount * USD_TO_TWD : amount;
    const symbol = currency === "USD" ? "$" : "NTD$";
    return symbol + amountWithCommas(treated.toFixed(2));
  };

  const depositSuccess = () =>
    msgApi.success({ content: t("Credit deposit success!") });
  const depositError = () =>
    msgApi.error({ content: t("Credit deposit failed!") });

  return (
    <>
      {contextHolder}

      <button
        className="primary-btn"
        style={{ margin: 0 }}
        disabled
        onClick={openDepositModal}
      >
        <div className="credit-btn-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20.0996 8.70508C20.0996 7.67052 19.7796 6.86188 19.2754 6.32031C18.7778 5.78596 18.0349 5.43947 17.0352 5.43945H6.96387C5.96868 5.43946 5.22536 5.78519 4.72656 6.32031C4.22126 6.86241 3.90044 7.6715 3.90039 8.70508V15.293C3.90039 16.3285 4.22061 17.1373 4.72461 17.6787C5.22182 18.2128 5.96404 18.5596 6.96387 18.5596H17.0342C18.0346 18.5596 18.7778 18.2129 19.2754 17.6787C19.7797 17.1373 20.0996 16.3283 20.0996 15.293V8.70508ZM21.9004 15.293C21.9004 16.6997 21.4599 17.9741 20.5928 18.9053C19.7187 19.8437 18.4787 20.3594 17.0342 20.3594H6.96387C5.51965 20.3594 4.27995 19.8438 3.40625 18.9053C2.53954 17.9741 2.09961 16.6995 2.09961 15.293V8.70508C2.09966 7.29769 2.54269 6.02341 3.41016 5.09277C4.2842 4.15523 5.52289 3.63868 6.96387 3.63867H17.0352C18.4795 3.63869 19.7189 4.15529 20.5928 5.09375C21.4597 6.0249 21.9003 7.29883 21.9004 8.70508V15.293Z"
              fill="white"
            />
            <path
              d="M21 8.93359L21.0918 8.93848C21.5457 8.98446 21.9004 9.36791 21.9004 9.83398C21.9004 10.3001 21.5457 10.6835 21.0918 10.7295L21 10.7344H3C2.50294 10.7344 2.09961 10.331 2.09961 9.83398C2.09961 9.33693 2.50294 8.93359 3 8.93359H21Z"
              fill="white"
            />
            <path
              d="M10.1455 14.2695L10.2373 14.2744C10.6911 14.3205 11.0449 14.7039 11.0449 15.1699C11.0449 15.6359 10.6911 16.0194 10.2373 16.0654L10.1455 16.0703H7.17676C6.6797 16.0703 6.27637 15.667 6.27637 15.1699C6.27637 14.6729 6.6797 14.2695 7.17676 14.2695H10.1455Z"
              fill="white"
            />
          </svg>
          <span>{t("Recharge Now")}</span>
        </div>
      </button>

      <Modal
        title={null}
        open={despositBalanceModal}
        footer={null}
        onCancel={handleCancel}
        width={400}
        centered
        destroyOnClose
        className="recharge-modal"
      >
        <div className="recharge-header">
          <h2>{t("Recharge")}</h2>
          <div className="credit-info">
            <span>{t("Current Credits")}:</span>
            <strong>{Number(credit).toFixed(4)}</strong>
          </div>
        </div>

        <Formik
          validateOnMount
          initialValues={{
            amount: 25, // ✅ CHANGED: number, not string
            currency: "USD" as "USD" | "NTD",
            payment_method: "PayPal" as "PayPal" | "Green",
          }}
          validationSchema={depositSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              setLoading(true);
              const body = {
                ...values,
                payment_id: RADNOM_PAYMENT_ID,
                status: "success",
                transaction_at: Date.now(),
              };
              const res = await depositBalance(body);
              const data = res.data;
              if (data?.msg === "success") {
                dispatch(updateCredit(data.data.balance));
                depositSuccess();
                resetForm();
                setDespositBalanceModal(false); // ✅ CHANGED: close only on success
              } else {
                depositError();
              }
            } catch (e) {
              console.error(e);
              depositError();
            } finally {
              setLoading(false); // ✅ CHANGED: always clear loading
            }
          }}
        >
          {({ values, setFieldValue, isValid }) => (
            <Form className="recharge-form">
              <div className="form-block">
                <label>{t("Credit Amount")}</label>
                <div className="amount-options">
                  {[25, 50, 100].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className={values.amount === amt ? "active" : ""}
                      onClick={() => setFieldValue("amount", amt)}
                    >
                      ${amt}
                    </button>
                  ))}
                  <InputNumber
                    placeholder="Other"
                    min={1} // or 25
                    value={values.amount} // keep in sync with Formik
                    onChange={(v) => setFieldValue("amount", Number(v) || 0)}
                    parser={(val?: string) =>
                      parseInt((val || "").replace(/[^\d]/g, ""), 10)
                    }
                    formatter={(val?: number | string) =>
                      `$${val ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    className={
                      values.amount !== 25 &&
                      values.amount !== 50 &&
                      values.amount !== 100 &&
                      values.amount > 0
                        ? "has-value"
                        : ""
                    }
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div className="form-block">
                <label>{t("Currency")}</label>
                <div className="currency-row">
                  <div
                    className={`currency-box ${
                      values.currency === "NTD" ? "active" : ""
                    }`}
                    onClick={() => setFieldValue("currency", "NTD")}
                  >
                    🇹🇼 NT${(Number(values.amount || 0) * USD_TO_TWD).toFixed(2)}
                  </div>
                  <div
                    className={`currency-box ${
                      values.currency === "USD" ? "active" : ""
                    }`}
                    onClick={() => setFieldValue("currency", "USD")}
                  >
                    🇺🇸 US${Number(values.amount || 0).toFixed(2)}
                  </div>
                </div>
                <small>1 USD = {USD_TO_TWD} TWD</small>
              </div>

              <div className="form-block">
                <label>{t("Payment Method")}</label>
                <div className="payment-options">
                  <div
                    className={`method ${
                      values.payment_method === "PayPal" ? "active" : ""
                    }`}
                    onClick={() => setFieldValue("payment_method", "PayPal")}
                  >
                    <img src="/images/icons/paypal.png" alt="PayPal" />
                  </div>
                  <div
                    className={`method ${
                      values.payment_method === "Green" ? "active" : ""
                    }`}
                    onClick={() => setFieldValue("payment_method", "Green")}
                  >
                    <img src="/images/icons/ecpay.png" alt="ECPay" />
                  </div>
                </div>
              </div>

              <div className="price-row">
                <label>{t("Price")}</label>
                <div className="price-row-details">
                  <span>{t("Total Price")}:</span> &nbsp;
                  <span>
                    {handledTotalCost(
                      Number(values.amount || 0),
                      values.currency,
                    )}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                className="pay-now-btn"
                disabled={!isValid || loading}
              >
                {loading ? t("Processing...") : t("Pay Now")}
              </button>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};
